import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo, gql } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash';

import { HeaderComponent } from '../header/header.component';
import { NavComponent } from '../navigation/nav.component';
import { AuthStore } from '@store/auth.store';
import { PaymentMethod } from '@interfaces/models.interface';
import { isUnsupportedByApi } from '../../../shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';

const PAYMENT_METHOD_FIELDS = `
  paymentMethodId
  title
  description
  icon
  category
  gateway
  type
  isActive
  allowExpress
  allowTnvs
  allowFood
  allowGrocery
  allowTaxi
  dateCreated
  dateUpdated
`;

// Not the app-facing `paymentMethods(filterService)` query: that one hides
// inactive rows, so a method switched off here could never be found again.
const PAYMENT_METHOD_LIST = gql`
  query paymentMethodList {
    paymentMethodList { ${PAYMENT_METHOD_FIELDS} }
  }
`;

const CREATE_PAYMENT_METHOD = gql`
  mutation createPaymentMethod($input: CreatePaymentMethodInput!) {
    createPaymentMethod(input: $input) { ${PAYMENT_METHOD_FIELDS} }
  }
`;

const UPDATE_PAYMENT_METHOD = gql`
  mutation updatePaymentMethod($input: UpdatePaymentMethodInput!) {
    updatePaymentMethod(input: $input) { ${PAYMENT_METHOD_FIELDS} }
  }
`;

/**
 * `type` is a behaviour key, not a label — these are the only values anything
 * downstream can process, so the form offers them as a list rather than a text
 * box. A method saved with `maya` instead of `paymaya` would still appear in
 * the app's picker, get chosen by a customer, and then fail at checkout.
 */
export const PAYMENT_METHOD_TYPES: { value: string; label: string; category: string }[] = [
  { value: 'origin', label: 'origin — passenger pays cash at the end of a trip', category: 'cash' },
  { value: 'sender', label: 'sender — the sender pays (Express)', category: 'cash' },
  { value: 'receipient', label: 'receipient — the recipient pays (Express)', category: 'cash' },
  { value: 'gcash', label: 'gcash — GCash via the gateway', category: 'ewallet' },
  { value: 'grab_pay', label: 'grab_pay — GrabPay via the gateway', category: 'ewallet' },
  { value: 'paymaya', label: 'paymaya — Maya via the gateway', category: 'ewallet' },
];

export const PAYMENT_CATEGORIES = [
  { value: 'cash', label: 'Cash — settles with the driver' },
  { value: 'ewallet', label: 'E-wallet — settles through the gateway' },
];

export const PAYMENT_GATEWAYS = [
  { value: 'cash', label: 'Cash (no gateway)' },
  { value: 'paymongo', label: 'PayMongo' },
];

type PaymentMethodForm = {
  title: string;
  description: string;
  icon: string;
  category: string;
  gateway: string;
  type: string;
  isActive: boolean;
  allowExpress: boolean;
  allowTnvs: boolean;
  allowFood: boolean;
  allowGrocery: boolean;
  allowTaxi: boolean;
};

/**
 * Payment methods (CMS) — add and edit.
 *
 * No delete, deliberately: historical bookings store `payment_method_id`, so
 * removing a row would orphan them. Switching a method inactive takes it out
 * of the apps' pickers and leaves the history intact.
 */
@Component({
  selector: 'app-payment-methods-cms',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, NavComponent],
  templateUrl: './payment-methods-cms.component.html',
  styleUrl: './payment-methods-cms.component.css',
})
export class PaymentMethodsCmsComponent implements OnInit {
  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private router: Router,
    private authStore: AuthStore,
    private permissions: AdminPermissionService,
  ) {}

  /** Editing is offered only with `payment_methods.manage` — the API refuses it otherwise. */
  get canManage(): boolean {
    return this.permissions.can(AdminPermission.PaymentMethodsManage);
  }

  public readonly types = PAYMENT_METHOD_TYPES;
  public readonly categories = PAYMENT_CATEGORIES;
  public readonly gateways = PAYMENT_GATEWAYS;

  public methods: PaymentMethod[] = [];
  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' = 'none';

  public showDetails = false;
  public enableEdit = false;
  public isSaving = false;
  public detailViewed: PaymentMethod | null = null;

  public form: PaymentMethodForm = this.emptyForm();
  public addForm: PaymentMethodForm = this.emptyForm();

  private emptyForm(): PaymentMethodForm {
    return {
      title: '',
      description: '',
      icon: '',
      category: 'ewallet',
      gateway: 'paymongo',
      type: 'gcash',
      isActive: true,
      allowExpress: true,
      allowTnvs: true,
      allowFood: true,
      allowGrocery: true,
      allowTaxi: false,
    };
  }

  ngOnInit(): void {
    this.fetchMethods();
  }

  typeLabel(type: string): string {
    return this.types.find((t) => t.value === type)?.value ?? type;
  }

  /**
   * A cash category with a gateway type (or the reverse) is the mistake this
   * form can still allow, so it is called out rather than silently saved.
   */
  mismatchWarning(form: PaymentMethodForm): string | null {
    const type = this.types.find((t) => t.value === form.type);
    if (!type) return null;
    if (type.category !== form.category) {
      return `Type "${form.type}" is normally used with the ${type.category} category.`;
    }
    if (form.category === 'cash' && form.gateway !== 'cash') {
      return 'A cash method normally has the "cash" gateway.';
    }
    if (form.category === 'ewallet' && form.gateway === 'cash') {
      return 'An e-wallet method normally has a real gateway.';
    }
    return null;
  }

  fetchMethods(): void {
    this.isLoading = true;
    this.loadError = 'none';

    this.apollo
      .watchQuery({ fetchPolicy: 'network-only', query: PAYMENT_METHOD_LIST })
      .valueChanges.subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.methods = cloneDeep(res?.data?.['paymentMethodList'] ?? []);

          if (this.detailViewed) {
            const refreshed = this.methods.find(
              (m) => m.paymentMethodId === this.detailViewed!.paymentMethodId,
            );
            if (refreshed) this.detailViewed = refreshed;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (this.handledAuthError(error)) return;
          this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
        },
      });
  }

  viewDetails(method: PaymentMethod): void {
    this.detailViewed = method;
    this.showDetails = true;
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  returnToList(): void {
    this.showDetails = false;
    this.enableEdit = false;
    this.detailViewed = null;
    this.form = this.emptyForm();
  }

  enableEditForm(): void {
    this.enableEdit = true;
    this.resetFormFromRecord();
  }

  disableEditForm(): void {
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  private resetFormFromRecord(): void {
    const r = this.detailViewed;
    if (!r) {
      this.form = this.emptyForm();
      return;
    }

    this.form = {
      title: r.title ?? '',
      description: r.description ?? '',
      icon: r.icon ?? '',
      category: r.category ?? 'ewallet',
      gateway: r.gateway ?? 'paymongo',
      type: r.type ?? 'gcash',
      isActive: r.isActive,
      allowExpress: r.allowExpress,
      allowTnvs: r.allowTnvs,
      allowFood: r.allowFood,
      allowGrocery: r.allowGrocery,
      allowTaxi: r.allowTaxi,
    };
  }

  private validationError(form: PaymentMethodForm): string | null {
    if (!form.title.trim()) return 'Enter a title.';
    if (form.title.trim().length > 50) return 'Title must not exceed 50 characters.';
    if (!form.description.trim()) return 'Enter a description.';
    if (form.description.trim().length > 200)
      return 'Description must not exceed 200 characters.';
    if (!form.icon.trim()) return 'Enter an icon.';
    if (!this.categories.some((c) => c.value === form.category)) return 'Choose a category.';
    if (!this.gateways.some((g) => g.value === form.gateway)) return 'Choose a gateway.';
    if (!this.types.some((t) => t.value === form.type)) return 'Choose a type.';
    return null;
  }

  // ----- add -----

  openAdd(modal: TemplateRef<any>): void {
    this.addForm = this.emptyForm();
    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  createMethod(successModal: TemplateRef<any>): void {
    const error = this.validationError(this.addForm);
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.isSaving = true;

    this.apollo
      .mutate({
        mutation: CREATE_PAYMENT_METHOD,
        variables: { input: this.payloadOf(this.addForm) },
      })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          const created: PaymentMethod | undefined = data?.['createPaymentMethod'];
          if (!created) {
            this.toastr.error('The payment method was not created. Please try again.');
            return;
          }

          this.methods = [...this.methods, cloneDeep(created)];
          this.modalService.dismissAll();
          this.modalService.open(successModal, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
          });
          this.fetchMethods();
        },
        error: (error) => {
          this.isSaving = false;
          if (this.handledAuthError(error)) return;
          this.toastr.error(error?.message || 'Error creating the payment method');
        },
      });
  }

  private payloadOf(form: PaymentMethodForm) {
    return {
      title: form.title.trim(),
      description: form.description.trim(),
      icon: form.icon.trim(),
      category: form.category,
      gateway: form.gateway,
      type: form.type,
      isActive: form.isActive,
      allowExpress: form.allowExpress,
      allowTnvs: form.allowTnvs,
      allowFood: form.allowFood,
      allowGrocery: form.allowGrocery,
      allowTaxi: form.allowTaxi,
    };
  }

  // ----- edit -----

  confirmUpdate(modal: TemplateRef<any>): void {
    const error = this.validationError(this.form);
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  updateMethod(successModal: TemplateRef<any>): void {
    const record = this.detailViewed;
    const error = this.validationError(this.form);
    if (!record || error) {
      if (error) this.toastr.error(error);
      return;
    }

    this.isSaving = true;

    this.apollo
      .mutate({
        mutation: UPDATE_PAYMENT_METHOD,
        variables: {
          input: { paymentMethodId: record.paymentMethodId, ...this.payloadOf(this.form) },
        },
      })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          const updated: PaymentMethod | undefined = data?.['updatePaymentMethod'];
          if (!updated) {
            this.toastr.error('The payment method was not updated. Please try again.');
            return;
          }

          this.detailViewed = cloneDeep(updated);
          this.methods = this.methods.map((m) =>
            m.paymentMethodId === updated.paymentMethodId ? cloneDeep(updated) : m,
          );
          this.resetFormFromRecord();

          this.modalService.dismissAll();
          this.enableEdit = false;
          this.modalService.open(successModal, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
          });
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          if (this.handledAuthError(error)) return;
          this.toastr.error(error?.message || 'Error updating the payment method');
        },
      });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  private handledAuthError(error: any): boolean {
    // Refused by the admin's roles, not a dead session: say so and stay signed in.
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return true;
    }

    const isAuthError =
      error?.message === 'Unauthorized' ||
      error?.networkError?.status === 403 ||
      error?.graphQLErrors?.[0]?.extensions?.status === 403 ||
      error?.errors?.[0]?.extensions?.status === 403;

    if (!isAuthError) return false;

    this.authStore.logout().subscribe(() => {
      this.router.navigate(['/admin/login']);
    });
    return true;
  }
}
